/**
 * Iter 252 — Manager Stats Dashboard pour le Traducteur + Coach IA.
 *
 * Accessible aux managers (role tenant_admin/manager/owner/admin).
 * Montre :
 *  - KPIs globaux 30j (logs, suggestions, acceptance %)
 *  - Top agents par utilisation du coach
 *  - Top intents détectés
 *  - Top objections du cookbook (par used_count)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  BarChart3, Users, Brain, Trophy, Sparkles, Loader2, ArrowRight, BookOpenCheck,
  CalendarRange, RefreshCw, TrendingUp, Lightbulb, FileText, Mail, Send,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend, RadialBarChart, RadialBar,
} from "recharts";

const INTENT_COLORS = {
  objection: "#f43f5e",
  question: "#06b6d4",
  interest: "#10b981",
  closing_window: "#f59e0b",
  risk: "#ef4444",
  small_talk: "#94a3b8",
  other: "#64748b",
};

export default function ManagerCoachStatsPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Iter 253 — Tab + weekly digests state
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") || "overview"));
  const [digests, setDigests] = useState([]);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestGen, setDigestGen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/aircall/translator/coach/stats/team");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Erreur");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Iter 253 — Load weekly digests when tab is opened
  useEffect(() => {
    if (tab !== "weekly") return;
    let cancelled = false;
    setDigestLoading(true);
    api.get("/aircall/translator/coach/weekly-digest")
      .then((r) => { if (!cancelled) setDigests(r.data.items || []); })
      .catch(() => {})
      .finally(() => !cancelled && setDigestLoading(false));
    return () => { cancelled = true; };
  }, [tab, api]);

  const generateDigest = async () => {
    setDigestGen(true);
    try {
      const r = await api.post("/aircall/translator/coach/weekly-digest", { days: 7, force_regenerate: true });
      setDigests((p) => [r.data, ...p.filter((d) => d.digest_id !== r.data.digest_id)]);
      toast.success("Digest hebdomadaire régénéré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setDigestGen(false); }
  };

  if (loading) return (
    <div className="p-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
  );

  if (error) return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-6 text-rose-200 text-sm" data-testid="manager-stats-error">
        {error}
      </div>
    </div>
  );

  const intentColor = {
    objection: "bg-rose-500/20 text-rose-300",
    question: "bg-cyan-500/20 text-cyan-300",
    interest: "bg-emerald-500/20 text-emerald-300",
    closing_window: "bg-amber-500/20 text-amber-300",
    risk: "bg-rose-500/20 text-rose-300",
    small_talk: "bg-slate-500/20 text-slate-300",
    other: "bg-slate-500/20 text-slate-300",
  };

  // Iter 252.1 — Recharts datasets
  const intentChartData = (data.top_intents || []).map((it) => ({
    name: it.intent,
    count: it.count,
    fill: INTENT_COLORS[it.intent] || INTENT_COLORS.other,
  }));

  const agentsChartData = (data.top_agents || []).slice(0, 8).map((a) => ({
    name: a.name && a.name.length > 14 ? a.name.slice(0, 12) + "…" : (a.name || "?"),
    sessions: a.logs,
    used: a.used,
    spoken: a.spoken,
    dismissed: a.dismissed,
    acceptance: a.acceptance_pct,
  }));

  const radialData = [{
    name: "Adoption",
    value: data.overall_acceptance_pct || 0,
    fill: data.overall_acceptance_pct >= 50 ? "#10b981" : "#f43f5e",
  }];

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="manager-coach-stats-page">
      <PageHeader
        icon={<BarChart3 />}
        title="Coach IA — Statistiques équipe"
        description="Vue manager : qui utilise le Coach IA, quelles objections sont les plus traitées, et avec quel taux d'adoption ?"
      />

      {/* Iter 253 — Tabs (Overview vs Weekly digest) */}
      <div className="flex items-center gap-2 border-b border-white/5" data-testid="manager-tabs">
        <button
          onClick={() => setTab("overview")}
          className={`px-3 py-2 text-sm font-bold border-b-2 -mb-px ${tab === "overview" ? "text-cyan-400 border-cyan-400" : "text-slate-400 border-transparent hover:text-white"}`}
          data-testid="tab-overview"
        >
          <BarChart3 className="w-3.5 h-3.5 inline mr-1.5" /> Vue d'ensemble (30j)
        </button>
        <button
          onClick={() => setTab("weekly")}
          className={`px-3 py-2 text-sm font-bold border-b-2 -mb-px ${tab === "weekly" ? "text-purple-400 border-purple-400" : "text-slate-400 border-transparent hover:text-white"}`}
          data-testid="tab-weekly"
        >
          <CalendarRange className="w-3.5 h-3.5 inline mr-1.5" /> Digest hebdomadaire IA
        </button>
      </div>

      {tab === "weekly" ? (
        <WeeklyDigestSection
          api={api}
          digests={digests}
          loading={digestLoading}
          onGenerate={generateDigest}
          generating={digestGen}
        />
      ) : (
        <>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="manager-stats-kpis">
        <Kpi label="Agents actifs (30j)" value={data.unique_agents} color="text-cyan-400" />
        <Kpi label="Sessions analysées" value={data.total_logs} color="text-purple-400" />
        <Kpi label="Suggestions générées" value={data.total_suggestions} color="text-amber-400" />
        <Kpi label="Adoptées" value={`${data.total_used + data.total_spoken}`} color="text-emerald-400" />
        {/* Radial gauge */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-1 relative" data-testid="kpi-radial">
          <ResponsiveContainer width="100%" height={110}>
            <RadialBarChart innerRadius="65%" outerRadius="100%" data={radialData} startAngle={210} endAngle={-30}>
              <RadialBar background={{ fill: "#1f1828" }} dataKey="value" cornerRadius={8} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className={`text-2xl font-bold ${data.overall_acceptance_pct >= 50 ? "text-emerald-400" : "text-rose-400"}`}>
              {data.overall_acceptance_pct}%
            </div>
            <div className="text-[10px] text-slate-400">Taux d'adoption</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top agents — Bar chart Recharts */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-bold text-white">Top utilisateurs du Coach (sessions)</h3>
          </div>
          {agentsChartData.length === 0 ? (
            <div className="text-center text-xs text-slate-500 py-6">Aucune utilisation enregistrée</div>
          ) : (
            <div data-testid="manager-top-agents-chart">
              <ResponsiveContainer width="100%" height={Math.max(220, agentsChartData.length * 32)}>
                <BarChart data={agentsChartData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" tick={{ fill: "#cbd5e1", fontSize: 11 }} width={90} />
                  <Tooltip
                    contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: "#fff" }}
                    cursor={{ fill: "rgba(6,182,212,0.05)" }}
                  />
                  <Bar dataKey="used" stackId="a" fill="#10b981" name="Utilisée" />
                  <Bar dataKey="spoken" stackId="a" fill="#06b6d4" name="Dite (TTS)" />
                  <Bar dataKey="dismissed" stackId="a" fill="#64748b" name="Ignorée" />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-4 text-[10px] text-slate-400 pt-2">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"></span>Utilisée</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-500"></span>Dite</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-500"></span>Ignorée</span>
              </div>
            </div>
          )}
        </div>

        {/* Top intents — Pie chart */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-bold text-white">Intentions détectées</h3>
          </div>
          {intentChartData.length === 0 ? (
            <div className="text-center text-xs text-slate-500 py-6">Aucune intention détectée</div>
          ) : (
            <div data-testid="manager-top-intents-chart">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={intentChartData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    label={(entry) => `${entry.count}`}
                    labelLine={false}
                  >
                    {intentChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} stroke="#14101c" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: "#fff" }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-2 flex-wrap pt-2">
                {intentChartData.map((it) => (
                  <span key={it.name} className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-bold ${intentColor[it.name] || intentColor.other}`}>
                    {it.name} ({it.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top objections */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-bold text-white">Top objections (cookbook)</h3>
          </div>
          <Link to="/admin/objection-cookbook" className="text-xs text-cyan-400 hover:underline flex items-center gap-1">
            Ouvrir le cookbook complet <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {data.top_objections.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-6">
            <BookOpenCheck className="w-8 h-8 text-slate-700 mx-auto mb-2" />
            Le cookbook est vide. Il se remplit automatiquement quand les commerciaux utilisent les suggestions du Coach IA.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="manager-top-objections">
            {data.top_objections.map((obj) => (
              <div key={obj.objection_id} className="bg-white/[0.02] border border-white/5 rounded p-2.5" data-testid={`top-obj-${obj.objection_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs text-white font-medium truncate">{obj.label}</span>
                  <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 font-bold">
                    ×{obj.used_count}
                  </span>
                </div>
                {obj.agent_native && (
                  <div className="text-[11px] text-slate-300 italic line-clamp-2">{obj.agent_native}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[10px] text-slate-600 text-right pt-2">
        <Sparkles className="w-2.5 h-2.5 inline mr-1" /> Période : 30 derniers jours
      </div>
        </>
      )}
    </div>
  );
}

// Iter 253 — Weekly Digest section
function WeeklyDigestSection({ api, digests, loading, onGenerate, generating }) {
  const latest = digests && digests[0];
  return (
    <div className="space-y-5" data-testid="weekly-digest-section">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-bold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" /> Copilote manager hebdomadaire
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            GPT-5.2 synthétise la semaine de votre équipe : performances, objections émergentes et recommandations.
          </div>
        </div>
        <Button
          onClick={onGenerate}
          disabled={generating}
          className="bg-purple-500 hover:bg-purple-400 text-white"
          data-testid="generate-digest-btn"
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          {latest ? "Régénérer cette semaine" : "Générer maintenant"}
        </Button>
      </div>

      <EmailDigestSettings api={api} />

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-400" /></div>
      ) : digests.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl py-12 text-center" data-testid="no-digests">
          <FileText className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="text-sm text-slate-400">Aucun digest encore généré</div>
          <div className="text-[11px] text-slate-600 mt-2 max-w-md mx-auto">
            Cliquez sur "Générer maintenant" pour obtenir le résumé de la semaine.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {digests.map((d) => (
            <DigestCard key={d.digest_id} d={d} highlight={d.digest_id === latest.digest_id} />
          ))}
        </div>
      )}
    </div>
  );
}

// Iter 263 — Abonnement email hebdo du digest (Resend)
function EmailDigestSettings({ api }) {
  const [s, setS] = useState(null);
  const [sending, setSending] = useState(false);
  const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  useEffect(() => {
    api.get("/coach-digest/email-settings").then(r => setS(r.data)).catch(() => {});
  }, [api]);

  if (!s) return null;

  const save = async (patch) => {
    try {
      const r = await api.put("/coach-digest/email-settings", { enabled: s.enabled, weekday: s.weekday, ...patch });
      setS(prev => ({ ...prev, ...r.data }));
      toast.success("Abonnement email mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/coach-digest/email-send", { days: 7 });
      toast.success(`Digest envoyé à ${(r.data.recipients || []).length} manager(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    setSending(false);
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="digest-email-settings">
      <div className="flex items-center gap-3 flex-wrap">
        <Mail className="w-4 h-4 text-purple-400" />
        <div>
          <div className="text-sm font-bold text-white">Recevoir par email chaque semaine</div>
          <div className="text-[11px] text-slate-500">
            Envoyé à tous les managers du tenant ({(s.recipients_preview || []).length}) : {(s.recipients_preview || []).slice(0, 3).join(", ")}{(s.recipients_preview || []).length > 3 ? "…" : ""}
          </div>
        </div>
        <Switch
          checked={!!s.enabled}
          onCheckedChange={(v) => { setS(prev => ({ ...prev, enabled: v })); save({ enabled: v }); }}
          data-testid="digest-email-switch"
        />
        {s.enabled && (
          <select
            value={s.weekday ?? 0}
            onChange={(e) => { const w = parseInt(e.target.value); setS(prev => ({ ...prev, weekday: w })); save({ weekday: w }); }}
            className="bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-xs text-slate-300"
            data-testid="digest-email-weekday"
          >
            {WEEKDAYS.map((d, i) => <option key={i} value={i}>chaque {d.toLowerCase()}</option>)}
          </select>
        )}
      </div>
      <Button
        onClick={sendNow}
        disabled={sending}
        variant="outline"
        className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
        data-testid="digest-email-send-btn"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
        Envoyer par email maintenant
      </Button>
    </div>
  );
}

function DigestCard({ d, highlight }) {
  const dt = d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
  return (
    <div className={`rounded-xl p-5 space-y-4 ${highlight ? "bg-gradient-to-br from-purple-500/8 to-blue-500/8 border border-purple-500/30" : "bg-[#14101c] border border-white/5"}`} data-testid={`digest-${d.digest_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase">Digest généré le {dt}</div>
          <div className="text-sm font-bold text-white">Semaine {d.week_anchor} · {d.period_days} jours</div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-cyan-400"><strong>{d.sessions_count}</strong> sessions</span>
          <span className="text-purple-400"><strong>{d.total_suggestions}</strong> suggestions</span>
          <span className="text-emerald-400"><strong>{d.adoption_pct}%</strong> adoption</span>
        </div>
      </div>

      {d.ai_summary && (
        <div className="bg-white/[0.02] border border-white/5 rounded p-3 text-sm text-slate-100 leading-relaxed" data-testid="digest-summary">
          <div className="text-[10px] uppercase text-purple-300 mb-1.5 flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" /> Synthèse IA
          </div>
          {d.ai_summary}
        </div>
      )}

      {d.ai_recommendations && d.ai_recommendations.length > 0 && (
        <div>
          <div className="text-[10px] uppercase text-amber-300 mb-2 flex items-center gap-1">
            <Lightbulb className="w-2.5 h-2.5" /> Recommandations actionnables
          </div>
          <ul className="space-y-1.5">
            {d.ai_recommendations.map((rec, i) => (
              <li key={i} className="text-xs text-amber-100 bg-amber-500/5 border border-amber-500/15 rounded p-2 flex gap-2" data-testid={`rec-${i}`}>
                <TrendingUp className="w-3 h-3 mt-0.5 text-amber-400 shrink-0" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.new_objections && d.new_objections.length > 0 && (
        <div>
          <div className="text-[10px] uppercase text-rose-300 mb-2">Objections clés de la semaine</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {d.new_objections.slice(0, 4).map((o) => (
              <div key={o.objection_id} className="bg-white/[0.02] border border-white/5 rounded p-2 text-[11px]">
                <div className="text-white font-medium truncate">{o.label}</div>
                <div className="text-slate-500">Utilisée {o.used_count}×</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-3" data-testid={`kpi-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-slate-400 mt-1">{label}</div>
    </div>
  );
}
